import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useLocale } from '@/i18n/locale-context';
import { setTrackerLocale, startTracker, trackPageView } from '@/lib/analytics';
import { useScrollRestoration, useSearchShortcut } from '@/lib/dom';
import { useBreaking } from '@/lib/use-breaking';
import { SearchOverlay } from '@/components/search';
import { BackToTop } from './BackToTop';
import { ConsentNotice } from './ConsentNotice';
import { Footer } from './Footer';
import { Header } from './Header';
import { NavDrawer, NavRail } from './NavRail';
import { OfflineBanner } from './OfflineBanner';
import { Ticker } from './Ticker';

/**
 * The frame every public page renders inside — PRD §28 and the supplied design's `.shell`.
 *
 * The structure is deliberately semantic rather than a stack of divs: `<aside>` for the
 * rail, one `<main id="main">` that the skip link targets, and the ticker above both. An
 * accessibility floor (§2) is cheapest to hold when the landmarks are right from the
 * first phase that draws them.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { locale, t } = useLocale();
  // The ticker owns its own data from Phase 11: server-rendered on first paint, then
  // live over SSE (`useBreaking`), rather than a prop threaded down from the route.
  const breaking = useBreaking(locale);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigationType = useNavigationType();

  useSearchShortcut(() => setSearchOpen(true));

  /**
   * One page view per route (Phase 16). In the shell rather than in each page, because a
   * page added later must not be able to forget it — and the shell is the only component
   * that renders on every route.
   *
   * `location.pathname` alone is the dependency: a query string is a filter on the same
   * page, and counting `?page=2` as a separate page would turn the popular-pages report
   * into a list of pagination links. `startTracker` is idempotent.
   */
  useEffect(() => {
    startTracker(locale);
    setTrackerLocale(locale);
    trackPageView(location.pathname);
  }, [locale, location.pathname]);
  // Keyed by path rather than by the router's location key, so the position survives a
  // reload — the key is regenerated on every fresh navigation.
  useScrollRestoration(location.pathname + location.search, navigationType);

  return (
    <div className="mx-auto min-h-screen max-w-shell bg-white shadow-shell">
      {/* First focusable element on the page, and translated — an English escape hatch on
          an Assamese page is exactly the kind of seam §1.15 exists to prevent. */}
      <a className="skip-link" href="#main">
        {t('action.skipToContent')}
      </a>
      <OfflineBanner />
      <Ticker items={breaking.items} connection={breaking.status} isPending={breaking.isPending} />

      <div className="flex">
        <NavRail />
        <NavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />

        <div className="flex min-w-0 flex-1 flex-col">
          <Header
            onOpenMenu={() => setMenuOpen(true)}
            onOpenSearch={() => setSearchOpen(true)}
            currentPath={location.pathname}
          />
          <main id="main" className="min-w-0 flex-1">
            {children}
          </main>
          <Footer />
        </div>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <BackToTop />
      <ConsentNotice />
    </div>
  );
}
